import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'
import DialogCloseButton from '../../../../components/Buttons/DialogCloseButton'
import { ChangeEvent, FormEvent, useContext, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import InputField from '../../../../components/InputFields/InputField'
import SelectField from '../../../../components/InputFields/SelectField'
import { EventDetailsDto, EventDto } from '../../../../interfaces/IEvent'
import { EventStatus } from '../../../../interfaces/IEventEnums'
import DateTimePickerField from '../../../../components/InputFields/DateTimePickerField'
import { FieldName } from '../../../../interfaces/IFields'
import MonoInputField from '../../../../components/InputFields/MonoInputField'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'

interface UpdateGenericDetailsProps {
  viewEvent: EventDto
  open: boolean
  onClose: () => void
}

function UpdateGenericDetails({ viewEvent, open, onClose }: UpdateGenericDetailsProps) {
  const [eventDetails, setEventDetails] = useState(viewEvent.eventDetails as EventDetailsDto)
  const { enqueueSnackbar } = useSnackbar()
  const { context } = useContext(EbAuthenticatedAppContext)
  const theme = useTheme()
  const smFullscreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [sendLoading, setSendLoading] = useState(false)

  const handleInputChange = (name: string, value: Date | string | null) => {
    if (name === 'name' || name === 'id') {
      setEventDetails((prevState) => ({
        ...prevState,
        responsible: {
          ...prevState.responsible,
          [name]: value,
        },
      }))
      return
    }

    setEventDetails((prevState: any) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleStartDateChange = (date: Date) => {
    const eventDetailsToDate = eventDetails.toDate ? new Date(eventDetails.toDate) : null

    if (date && eventDetailsToDate && date > eventDetailsToDate) {
      setEventDetails((prevState) => ({
        ...prevState,
        toDate: date,
      }))
    }

    setEventDetails((prevState) => ({
      ...prevState,
      fromDate: date,
    }))
  }

  const handleEndDateChange = (date: Date | null) => {
    const eventDetailsFromDate = new Date(eventDetails.fromDate)

    if (date && eventDetailsFromDate > date) {
      setEventDetails((prevState) => ({
        ...prevState,
        toDate: eventDetailsFromDate,
      }))

      return
    }

    setEventDetails((prevState) => ({
      ...prevState,
      toDate: date,
    }))
  }

  const handleEditEvent = async (e: FormEvent<HTMLFormElement>) => {
    setSendLoading(true)

    e.preventDefault()

    var response = await context.restApiClient.buildRequest(`/api/event/${viewEvent.id}/update`).serializeToJsonContent(eventDetails).putAsync()

    if (response.error) {
      if (response.error.errorCode === 409) {
        enqueueSnackbar(
          <Typography variant="body2">Hendelsen har blitt endret på siden sist du åpnet den. Ta vare på endringene og refresh siden.</Typography>,
          { variant: 'warning' },
        )
      } else {
        enqueueSnackbar(<Typography variant="body2">Klarte ikke lagre melding. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
    } else {
      enqueueSnackbar(<Typography variant="body2">Hendelsen er oppdatert.</Typography>, { variant: 'success' })
      onClose()
      return setSendLoading(false)
    }

    setSendLoading(false)
  }

  useEffect(() => {
    setEventDetails(viewEvent.eventDetails as EventDetailsDto)
  }, [viewEvent.eventDetails])

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      fullScreen={smFullscreen}
      scroll={'paper'}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '8px',
          backgroundColor: 'background.default',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="body1" sx={{
          fontWeight: 600
        }}>
          Endre hendelsesdetaljer
        </Typography>
        <DialogCloseButton handleClose={onClose} />
      </DialogTitle>
      <form
        onSubmit={(e) => {
          handleEditEvent(e)
          e.preventDefault()
        }}
        style={{
          display: 'contents',
        }}
      >
        <DialogContent dividers={true}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 12,
              }}
            >
              <InputField
                name={FieldName.Title}
                title="Navn på hendelse"
                size="medium"
                defaultValue={eventDetails.title}
                multiline={true}
                rows={2}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => handleInputChange(ev.target.name, ev.target.value)}
                maxLength={80}
                required={true}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <InputField
                name={FieldName.Name}
                title="Ansvarlig"
                size="medium"
                defaultValue={eventDetails.responsible.name}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => handleInputChange(ev.target.name, ev.target.value)}
                multiline={false}
                maxLength={30}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <SelectField
                name={FieldName.Status}
                title="Status"
                placeholder="Ingen valgt"
                selectOptions={Object.values(EventStatus)}
                defaultValue={eventDetails.status}
                value={eventDetails.status}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => handleInputChange(ev.target.name, ev.target.value)}
                required={true}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <DateTimePickerField
                name={FieldName.FromDate}
                title="Starttid"
                value={eventDetails.fromDate ? new Date(eventDetails.fromDate) : null}
                onChange={(date: Date) => handleStartDateChange(date)}
                required={true}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <DateTimePickerField
                name={FieldName.ToDate}
                title="Sluttid"
                disablePast={true}
                minDateTime={new Date(eventDetails.fromDate)}
                value={eventDetails.toDate ? new Date(eventDetails.toDate) : null}
                onChange={(date: Date | null) => handleEndDateChange(date)}
              />
            </Grid>
            <Grid size={12}>
              <InputField
                name={FieldName.ExternalReference}
                title="Ekstern referanse"
                size="medium"
                defaultValue={eventDetails.externalReference}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => handleInputChange(ev.target.name, ev.target.value)}
                maxLength={20}
              />
            </Grid>
            <Grid size={12}>
              <MonoInputField
                name={FieldName.Description}
                title="Beskrivelse"
                size="medium"
                defaultValue={eventDetails.description}
                onChange={(ev: ChangeEvent<HTMLInputElement>) => handleInputChange(ev.target.name, ev.target.value)}
                multiline={true}
                rows={13}
                maxLength={2000}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained" color="error" disabled={sendLoading}>
            Avbryt
          </Button>
          <Button type="submit" variant="contained" disabled={sendLoading} startIcon={sendLoading ? <LoadingIndicator size={'xsmall'} /> : null}>
            Lagre
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default UpdateGenericDetails
